import { useEffect, useId, useState } from 'react';
import { ChevronDown, House } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import type { AppearanceNavigationProps } from '../types';

/** Entries have already passed the shell's permission and feature filters. */
export function SimpleNavigation({
  groups,
  activeDomain,
  pathname,
  onNavigate,
}: AppearanceNavigationProps) {
  const { t } = useTranslation();
  const id = useId();
  const [expanded, setExpanded] = useState<string | null>(pathname === '/' ? null : activeDomain);
  useEffect(() => {
    setExpanded(pathname === '/' ? null : activeDomain);
  }, [activeDomain, pathname]);
  return (
    <nav aria-label={t('nav.label')} className="simple-navigation space-y-2 px-3 pb-5">
      <Link
        to="/"
        onClick={onNavigate}
        aria-current={pathname === '/' ? 'page' : undefined}
        className="simple-home flex min-h-11 items-center gap-3 rounded-lg px-3 py-3 font-semibold"
      >
        <House className="size-5" aria-hidden="true" />
        {t('appearance.start')}
      </Link>
      <p className="px-3 pt-5 pb-2 text-xs font-medium text-muted-foreground">
        {t('appearance.areas')}
      </p>
      {groups.map((group) => {
        const items = group.items.filter((item) => item.to !== '/');
        if (items.length === 0) return null;
        const open = expanded === group.id;
        return (
          <div key={group.id} data-domain={group.id} className="simple-domain">
            <button
              type="button"
              aria-expanded={open}
              aria-controls={`${id}-${group.id}`}
              className={cn(
                'flex min-h-11 w-full items-center gap-3 rounded-lg px-3 py-3 text-left text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                open && 'bg-secondary text-secondary-foreground',
              )}
              onClick={() => setExpanded(open ? null : group.id)}
            >
              <span aria-hidden="true" className="simple-domain-dot size-2 shrink-0 rounded-full" />
              <span className="flex-1">{group.label}</span>
              <ChevronDown
                aria-hidden="true"
                className={cn('size-4 shrink-0', open && 'rotate-180')}
              />
            </button>
            <ul id={`${id}-${group.id}`} hidden={!open} className="space-y-1 py-2 pl-4">
              {items.map((item) => {
                const active = pathname === item.to || pathname.startsWith(`${item.to}/`);
                return (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      onClick={onNavigate}
                      aria-current={active ? 'page' : undefined}
                      className={cn(
                        'flex min-h-11 items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        active
                          ? 'bg-primary/10 font-semibold text-primary'
                          : 'text-muted-foreground',
                      )}
                    >
                      <item.icon className="size-4 shrink-0" aria-hidden="true" />
                      <span>{item.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </nav>
  );
}
